import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { getAdminDashboard, getAdminPeriodStats } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Users, BookOpen, Calendar, ClipboardList, FileText, Award, Video, AlertCircle, ChevronRight } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';

function dateInput(value) {
  return [
    value.getFullYear(),
    String(value.getMonth() + 1).padStart(2, '0'),
    String(value.getDate()).padStart(2, '0'),
  ].join('-');
}

function monthRange(offset = 0) {
  const now = new Date();
  const first = new Date(now.getFullYear(), now.getMonth() + offset, 1);
  const last = new Date(now.getFullYear(), now.getMonth() + offset + 1, 0);
  return { from: dateInput(first), to: dateInput(last) };
}

function yearRange() {
  const now = new Date();
  return {
    from: dateInput(new Date(now.getFullYear(), 0, 1)),
    to: dateInput(new Date(now.getFullYear(), 11, 31)),
  };
}

function formatMoney(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString(undefined, {
    style: 'currency',
    currency: 'CAD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatNumber(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString();
}

function DashboardKpiCard({ label, value, icon: Icon, accent = false, loading = false, badge = null, money = false, path }) {
  const card = (
    <Card className={`card-hover h-full border border-[rgba(6, 43, 26,0.08)] shadow-sm transition-all ${path ? 'cursor-pointer hover:-translate-y-0.5 hover:border-[#D4AF37]/50 hover:shadow-md' : ''} ${accent ? 'gold-border-left' : ''}`}>
      <CardContent className="p-4">
        <div className="mb-3 flex items-center justify-between gap-3">
          <span className="text-xs font-semibold uppercase tracking-[0.1em] text-[#062B1A]/50">{label}</span>
          <Icon className="h-4 w-4 flex-shrink-0 text-[#D4AF37]" />
        </div>
        {loading ? (
          <div className="h-9 w-24 animate-pulse rounded-md bg-[#F7F8FA]" />
        ) : (
          <div className="flex flex-wrap items-end gap-2">
            <p className="text-3xl font-bold text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>
              {money ? formatMoney(value) : formatNumber(value)}
            </p>
            {badge}
            {path && <ChevronRight className="ml-auto h-5 w-5 text-[#062B1A]/25" />}
          </div>
        )}
      </CardContent>
    </Card>
  );
  return path ? <Link to={path} className="block h-full rounded-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-[#D4AF37]" aria-label={`Open ${label}`}>{card}</Link> : card;
}

export default function AdminDashboard() {
  const { loading: authLoading } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const defaultRange = useMemo(() => monthRange(0), []);
  const [activePreset, setActivePreset] = useState('this_month');
  const [appliedRange, setAppliedRange] = useState(defaultRange);
  const [customFrom, setCustomFrom] = useState(defaultRange.from);
  const [customTo, setCustomTo] = useState(defaultRange.to);
  const [periodStats, setPeriodStats] = useState(null);
  const [periodLoading, setPeriodLoading] = useState(false);
  const [periodError, setPeriodError] = useState('');
  const periodRequestId = useRef(0);

  const loadDashboard = useCallback(async () => {
    try {
      setLoading(true);
      const res = await getAdminDashboard();
      setData(res.data);
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || 'Could not load dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadPeriodStats = useCallback(async (range) => {
    const requestId = periodRequestId.current + 1;
    periodRequestId.current = requestId;
    setPeriodLoading(true);
    setPeriodError('');
    try {
      const res = await getAdminPeriodStats(range);
      if (periodRequestId.current !== requestId) return;
      setPeriodStats(res.data);
    } catch (err) {
      if (periodRequestId.current !== requestId) return;
      console.error(err);
      const message = err?.response?.data?.detail || 'Could not load period stats';
      setPeriodError(message);
      toast.error(message);
    } finally {
      if (periodRequestId.current === requestId) {
        setPeriodLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    if (!authLoading) loadDashboard();
  }, [authLoading, loadDashboard]);

  useEffect(() => {
    if (!authLoading) loadPeriodStats(appliedRange);
  }, [appliedRange, authLoading, loadPeriodStats]);

  const applyPreset = (preset) => {
    const next = preset === 'last_month' ? monthRange(-1) : preset === 'this_year' ? yearRange() : monthRange(0);
    setActivePreset(preset);
    setCustomFrom(next.from);
    setCustomTo(next.to);
    setAppliedRange(next);
  };

  const applyCustomRange = () => {
    if (!customFrom || !customTo) {
      const message = 'Choose both a From and To date';
      setPeriodError(message);
      toast.error(message);
      return;
    }
    if (customFrom > customTo) {
      const message = 'From date must be before or equal to To date';
      setPeriodError(message);
      toast.error(message);
      return;
    }
    setActivePreset('custom');
    setAppliedRange({ from: customFrom, to: customTo });
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          {[1,2,3,4].map(i => <div key={i} className="h-28 bg-white rounded-xl animate-pulse" />)}
        </div>
      </div>
    );
  }

  const stats = data?.stats || {};
  const kpis = [
    { label: 'Total Students', value: stats.total_students, icon: Users, accent: false, path: '/admin/students' },
    { label: 'Total Courses', value: stats.total_courses, icon: BookOpen, accent: true, path: '/admin/courses' },
    { label: 'Upcoming Sessions', value: stats.upcoming_sessions, icon: Video, accent: false, path: '/admin/live-sessions' },
    { label: 'Pending Grading', value: stats.pending_grading, icon: AlertCircle, accent: true, path: '/admin/submissions?status=submitted' },
  ];

  const catDist = data?.category_distribution || {};
  const pieData = Object.entries(catDist).map(([name, value]) => ({ name, value }));
  const COLORS = ['#062B1A', '#D4AF37', 'rgba(6, 43, 26,0.65)', 'rgba(212, 175, 55,0.65)', '#000'];

  const barData = [
    { name: 'Courses', total: stats.total_courses, published: stats.published_courses },
    { name: 'Exams', total: stats.total_exams, published: 0 },
    { name: 'Assignments', total: stats.total_assignments, published: 0 },
    { name: 'Submissions', total: stats.total_submissions, published: stats.pending_grading },
  ];
  // These three tiles used to come from an external accounting service and
  // showed an "unavailable" badge whenever it could not be reached. They are
  // now computed from the portal's own invoices and tuition ledger, so there is
  // no external call left to fail and no badge to show.
  const rangeQuery = `from=${encodeURIComponent(appliedRange.from)}&to=${encodeURIComponent(appliedRange.to)}`;
  const invoiceRange = `/admin/accounting/sales/invoices?tab=all&from=${encodeURIComponent(appliedRange.from)}&to=${encodeURIComponent(appliedRange.to)}`;
  const periodTiles = [
    { label: 'New Students', value: periodStats?.new_students, icon: Users, path: `/admin/students?date_field=created_at&${rangeQuery}` },
    { label: 'Course Starts', value: periodStats?.course_starts, icon: Calendar, accent: true, path: `/admin/students?date_field=start_date&${rangeQuery}` },
    { label: 'Course Ends', value: periodStats?.course_ends, icon: Calendar, path: `/admin/students?date_field=end_date&${rangeQuery}` },
    { label: 'Invoices', value: periodStats?.invoices_count, icon: FileText, accent: true, path: invoiceRange },
    { label: 'Payments', value: periodStats?.payments_count, icon: ClipboardList, path: invoiceRange },
    { label: '$ Received', value: periodStats?.payments_received, icon: Award, accent: true, money: true, path: invoiceRange },
    { label: 'Commissions', value: periodStats?.commissions_count, icon: ClipboardList, path: `/admin/commissions?${rangeQuery}` },
    { label: '$ Commission Paid', value: periodStats?.commission_paid, icon: Award, accent: true, money: true, path: `/admin/commissions?${rangeQuery}` },
  ];
  const presetClass = (preset) => (
    activePreset === preset
      ? 'bg-[#062B1A] text-white hover:bg-[#041B10]'
      : 'border-[rgba(6,43,26,0.16)] text-[#062B1A]'
  );

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h2 className="text-2xl font-semibold text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>Admin Dashboard</h2>
        <p className="text-sm text-[#062B1A]/60 mt-1">Overview of platform statistics and activity</p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        {kpis.map((kpi, idx) => (
          <DashboardKpiCard key={idx} {...kpi} />
        ))}
      </div>

      {/* Period Stats */}
      <section className="space-y-4">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <h3 className="text-lg font-semibold text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>Period Stats</h3>
            <p className="text-sm text-[#062B1A]/60">
              Live metrics for {appliedRange.from} to {appliedRange.to}
            </p>
          </div>
          <div className="flex flex-col gap-3 lg:items-end">
            <div className="flex flex-wrap gap-2">
              <Button type="button" variant="outline" size="sm" onClick={() => applyPreset('this_month')} className={presetClass('this_month')}>
                This month
              </Button>
              <Button type="button" variant="outline" size="sm" onClick={() => applyPreset('last_month')} className={presetClass('last_month')}>
                Last month
              </Button>
              <Button type="button" variant="outline" size="sm" onClick={() => applyPreset('this_year')} className={presetClass('this_year')}>
                This year
              </Button>
            </div>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
              <label className="flex flex-col gap-1 text-xs font-semibold uppercase tracking-[0.08em] text-[#062B1A]/50">
                From
                <Input type="date" value={customFrom} onChange={(event) => setCustomFrom(event.target.value)} className="min-w-[150px]" />
              </label>
              <label className="flex flex-col gap-1 text-xs font-semibold uppercase tracking-[0.08em] text-[#062B1A]/50">
                To
                <Input type="date" value={customTo} onChange={(event) => setCustomTo(event.target.value)} className="min-w-[150px]" />
              </label>
              <Button type="button" onClick={applyCustomRange} className="bg-[#062B1A] text-white hover:bg-[#041B10]">
                Apply
              </Button>
            </div>
          </div>
        </div>

        {periodError && (
          <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {periodError}
          </p>
        )}

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
          {periodTiles.map((tile) => (
            <DashboardKpiCard
              key={tile.label}
              {...tile}
              loading={periodLoading}
            />
          ))}
        </div>
      </section>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border border-[rgba(6, 43, 26,0.08)] shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>Platform Overview</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={barData}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(6, 43, 26,0.08)" />
                <XAxis dataKey="name" tick={{ fontSize: 12, fill: '#062B1A' }} />
                <YAxis tick={{ fontSize: 12, fill: '#062B1A' }} />
                <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid rgba(6, 43, 26,0.12)' }} />
                <Bar dataKey="total" fill="#062B1A" radius={[4, 4, 0, 0]} name="Total" />
                <Bar dataKey="published" fill="#D4AF37" radius={[4, 4, 0, 0]} name="Active" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="border border-[rgba(6, 43, 26,0.08)] shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>Course Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            {pieData.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie data={pieData} cx="50%" cy="50%" innerRadius={60} outerRadius={90} dataKey="value" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                    {pieData.map((entry, idx) => <Cell key={idx} fill={COLORS[idx % COLORS.length]} />)}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-sm text-[#062B1A]/50 text-center py-12">No data</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent submissions */}
      <Card className="border border-[rgba(6, 43, 26,0.08)] shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>Recent Submissions</CardTitle>
        </CardHeader>
        <CardContent>
          {data?.recent_submissions?.length === 0 ? (
            <p className="text-sm text-[#062B1A]/50 text-center py-6">No submissions yet</p>
          ) : (
            <div className="space-y-2">
              {data?.recent_submissions?.slice(0, 5).map(sub => (
                <div key={sub.id} className="flex items-center justify-between p-3 rounded-lg bg-[#F7F8FA] table-row-hover">
                  <div className="flex items-center gap-3">
                    <Badge variant="outline" className="text-[10px]">{sub.type}</Badge>
                    <span className="text-sm text-[#062B1A]">{sub.student_id?.slice(0, 8)}...</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge className={`text-[10px] border-0 ${sub.status === 'graded' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>{sub.status}</Badge>
                    {sub.status === 'graded' && <span className="text-sm font-medium text-[#062B1A]">{sub.score}/{sub.max_score}</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
